import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { BehaviorSubject,  from, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { inject } from '@angular/core';
import { AuthService } from '../../auth/auth.service';
import { ToastService } from '../toast/toast.service';
import { Router } from '@angular/router';
import { LocalStorageService } from '../local-storage/local-storage.service';
const isRefreshing = new BehaviorSubject<boolean>(false);
const refreshTokenSubject = new BehaviorSubject<string | undefined>(undefined);

export const tokenInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
    const authService = inject(AuthService);
    const toastService = inject(ToastService);
    const localStorageService = inject(LocalStorageService);
    const router: Router = inject(Router);
    const accessToken = localStorageService.getAccessToken();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    let r = request.clone();
    if (accessToken) {
        if (authService.isJwtExpired(accessToken, 59 * 5)) {
            return handleTokenExpiredKeycloak(r, next, authService, toastService, localStorageService, router);
        }
        r = addToken(r, accessToken);
    }
    return next(r).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
                return handleTokenExpiredKeycloak(r, next, authService, toastService, localStorageService, router);
            }
            toastService.showErrorViaToast(toastService.formateErrorMessage(error));
            return throwError(() => error);
        }),
    );
};

const addToken = (request: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
    return request.clone({
        setHeaders: {
            Authorization: `Bearer ${token}`,
        },
    });
};


const handleTokenExpiredKeycloak = (
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    toastService: ToastService,
    localStorageService: LocalStorageService,
    router: Router,
) => {
    if (!isRefreshing.value) {
        isRefreshing.next(true);
        refreshTokenSubject.next(undefined);
        const savedAccessToken = localStorageService.getAccessToken();
        const savedRefreshToken = localStorageService.getRefreshToken();
        let reinitialize$;
        if (authService.keycloak.authenticated == true) {
            reinitialize$ = from(Promise.resolve(true)); 
        } else {
            reinitialize$ = from(authService.keycloak.init({
                onLoad: 'check-sso',
                checkLoginIframe: false,
                token: savedAccessToken !== null ? savedAccessToken : undefined,
                refreshToken: savedRefreshToken !== null ? savedRefreshToken : undefined
            }));
        }
        return reinitialize$.pipe(
            switchMap(() => {
                return from(authService.keycloak.updateToken(30));
            }),
            switchMap(() => {
                isRefreshing.next(false);
                const newAccessToken = authService.keycloak.token!;
                const newRefreshToken = authService.keycloak.refreshToken!;
                localStorageService.setKeycloakTokens({
                    access_token: newAccessToken,
                    refresh_token: newRefreshToken
                }); 
                refreshTokenSubject.next(newAccessToken);
                return next(addToken(request, newAccessToken));
            }),
            catchError((error) => {
                isRefreshing.next(false);
                refreshTokenSubject.next(undefined);
                authService.keycloak?.clearToken(); 
                localStorageService.clearLocalStorage();
                authService.isLoggedIn.next(false);
                router.navigate(['/auth']);
                toastService.showErrorViaToast('Session expired. Please log in again.');
                return throwError(() => error);
            })
        );
    } else {
        return refreshTokenSubject.pipe(
            filter((token) => token !== undefined),
            take(1),
            switchMap((newAccessToken) => {
                return next(addToken(request, newAccessToken!));
            }),
        );
    }
};